import { parseIsoDateOrTimestamp } from '@wix/da-i18next/pkg/time/functions';
import omit from 'lodash/omit';
import {
  initialize,
  initializeSuccess,
  initializeTierTagsSuccess,
  initializeTierTagsError,
  deinitialize,
  submitDeviation,
  submitDeviationSuccess,
  deviationSaveError,
  refresh,
  refreshSuccess,
  setDefaultValues,
  scheduleDraftPublish,
  scheduleDraftPublishSuccess,
  scheduleDraftPublishError,
  groupDestinationsFetch,
  groupDestinationsFetchSuccess,
  groupDestinationsFetchError,
  deviationSaveCancel,
  publishDeviation,
  publishDeviationSuccess,
  publishDeviationError,
  setUploadStatus,
  updatePcpAttachments,
  removePcpAttachment,
} from '../actions';
import { DeviationSubmitPopupStoreSlice } from '../types';
import { SUBMIT_POPUP_ERROR_CODE } from '../constants';

export default {
  [submitDeviation.toString()]: (
    state,
    action
  ): DeviationSubmitPopupStoreSlice => ({
    ...state,
    formStatus: 'submitting',
    error: undefined,
  }),
  [submitDeviationSuccess.toString()]: state => ({
    ...state,
    formStatus: 'idle',
  }),
  [deviationSaveCancel.toString()]: state => ({
    ...state,
    formStatus: 'idle',
  }),
  [deviationSaveError.toString()]: (
    state,
    { payload: { errorCode, message } }: ReturnType<typeof deviationSaveError>
  ) => ({
    ...state,
    formStatus: 'error',
    error: { message, errorCode },
  }),
  [initialize.toString()]: (state, action: ReturnType<typeof initialize>) => ({
    ...state,
    response: undefined,
    formStatus: 'fetching',
  }),
  [initializeSuccess.toString()]: (
    state,
    action: ReturnType<typeof initializeSuccess>
  ) => ({
    ...state,
    response: { ...omit(action.payload, 'scheduledPublishTime') },
    scheduleTimestamp: action.payload.scheduledPublishTime,
    formStatus: 'idle',
  }),
  [refresh.toString()]: (state, action: ReturnType<typeof refresh>) => ({
    ...state,
    formStatus: 'fetching',
  }),
  [refreshSuccess.toString()]: (
    state,
    action: ReturnType<typeof refreshSuccess>
  ) => ({
    ...state,
    response: { ...action.payload },
    formStatus: 'idle',
  }),
  [deinitialize.toString()]: (
    state,
    action: ReturnType<typeof deinitialize>
  ) => ({
    ...state,
    error: undefined,
    response: undefined,
    formStatus: 'uninitialized',
  }),
  [initializeTierTagsSuccess.toString()]: (
    state,
    action: ReturnType<typeof initializeTierTagsSuccess>
  ) => ({
    ...state,
    tagsByTierId: {
      ...state.tagsByTierId,
      [action.payload.tierId]: action.payload.tags,
    },
  }),
  [initializeTierTagsError.toString()]: (
    state,
    action: ReturnType<typeof initializeTierTagsError>
  ) => ({
    ...state,
    error: {
      message: action.payload.errorMessage,
      errorCode: SUBMIT_POPUP_ERROR_CODE.TIERS_INITIALIZATION_ERROR,
    },
  }),
  [setDefaultValues.toString()]: (
    state,
    action: ReturnType<typeof setDefaultValues>
  ) => ({
    ...state,
    defaultValues: action.payload.defaultValues,
  }),
  [publishDeviation.toString()]: state => ({
    ...state,
    publish: { requestStatus: 'busy' },
  }),
  [publishDeviationSuccess.toString()]: state => ({
    ...state,
    publish: { requestStatus: 'idle' },
  }),
  [publishDeviationError.toString()]: state => ({
    ...state,
    publish: { requestStatus: 'idle' },
  }),
  [scheduleDraftPublish.toString()]: (
    state,
    action: ReturnType<typeof scheduleDraftPublish>
  ) => ({
    ...state,
    scheduleStatus: 'busy',
    schedulePendingTimestamp: action.payload.scheduleTs,
  }),
  [scheduleDraftPublishSuccess.toString()]: state => ({
    ...state,
    scheduleStatus: 'idle',
    schedulePendingTimestamp: undefined,
    scheduleTimestamp: state.schedulePendingTimestamp
      ? parseIsoDateOrTimestamp(state.schedulePendingTimestamp).toISOString()
      : undefined,
  }),
  [scheduleDraftPublishError.toString()]: state => ({
    ...state,
    scheduleStatus: 'idle',
    schedulePendingTimestamp: undefined,
  }),
  [setUploadStatus.toString()]: (
    state,
    { payload }: ReturnType<typeof setUploadStatus>
  ) => ({
    ...state,
    uploadStatus: {
      ...state.uploadStatus,
      [payload.uploader]: payload.status,
    },
  }),
  [updatePcpAttachments.toString()]: (
    state,
    { payload: { assets } }: ReturnType<typeof updatePcpAttachments>
  ) => ({
    ...state,
    response: {
      ...state.response,
      pcp: {
        ...state.response.pcp,
        assets: (state.response.pcp.assets || [])
          .filter(
            oldAsset =>
              !assets.find(newAsset => oldAsset.fileId === newAsset.fileId)
          )
          .concat(assets),
      },
    },
  }),
  [removePcpAttachment.toString()]: (
    state,
    { payload: { fileId } }: ReturnType<typeof removePcpAttachment>
  ) => ({
    ...state,
    response: {
      ...state.response,
      pcp: {
        ...state.response.pcp,
        assets: (state.response.pcp.assets || []).filter(
          asset => asset.fileId !== fileId
        ),
      },
    },
  }),
  [groupDestinationsFetch.toString()]: (state, action) => ({
    ...state,
    groupDestinations: {
      groupDestinations: [],
      inGroups: [],
      status: 'fetching',
      deviationId: action.payload.deviationId,
    },
  }),
  [groupDestinationsFetchSuccess.toString()]: (state, action) => ({
    ...state,
    groupDestinations: {
      ...state.groupDestinations,
      ...action.payload,
      status: 'idle',
    },
  }),
  [groupDestinationsFetchError.toString()]: state => ({
    ...state,
    groupDestinations: {
      ...state.groupDestinations,
      status: 'error',
    },
  }),
};
